<template>
  <ak-basic-container>
    <!-- Search and Filter bar -->
    <ak-box class="pb-none">
      <h4 class="color-grey mb-small">Surveys</h4>
      <ak-form class="flex" @submit="fetchSurveys">
        <ak-input
          v-model="searchFilter"
          type="search"
          icon="search"
          placeholder="Search Surveys"
          class="max-width-300"
          @right-icon-click="fetchSurveys"
        />
        <ak-button-group ghost class="ml bg-white">
          <ak-button
            :ghost="filter.status !== 'published'"
            color="grey-light"
            @click="setFilter('status', 'published')"
            >Published</ak-button
          >
          <ak-button
            :ghost="filter.status !== 'unpublished'"
            color="grey-light"
            @click="setFilter('status', 'unpublished')"
            >Unpublished</ak-button
          >
        </ak-button-group>
        <ak-button-group ghost class="ml bg-white">
          <ak-button
            :ghost="filter.endDate !== 'live'"
            color="grey-light"
            @click="setFilter('endDate', 'live')"
            >Live</ak-button
          >
          <ak-button
            :ghost="filter.endDate !== 'close'"
            color="grey-light"
            @click="setFilter('endDate', 'close')"
            >Closed</ak-button
          >
        </ak-button-group>
        <ak-button-group ghost class="ml bg-white">
          <ak-button
            :ghost="filter.access !== 'open'"
            color="grey-light"
            @click="setFilter('access', 'open')"
            >Open</ak-button
          >
          <ak-button
            :ghost="filter.access !== 'invite'"
            color="grey-light"
            @click="setFilter('access', 'invite')"
            >Invite Only</ak-button
          >
        </ak-button-group>
        <ak-button
          class="ml-auto"
          tag="router-link"
          :to="{ name: 'withdrawalRequests' }"
          >Withdrawal Requests</ak-button
        >
      </ak-form>
    </ak-box>
    <ak-box class="mb-huge">
      <ak-table
        v-model:current-page="currentPage"
        v-model:data="surveys"
        v-model:loading="loading"
        class="one-whole"
        :per-page="perPage"
        :total="total"
        :show-rows="shouldDisplayTableRows"
        :sticky-pagination="false"
        backend-pagination
        inset
        hoverable
        background
        paginated
        @page="onPageChange"
      >
        <template #default="{ row }">
          <!-- Name -->
          <ak-table-column label="Survey" field="name">
            <router-link
              class="block p-small"
              :to="{ name: 'editSurvey', params: { surveyId: row._id } }"
            >
              <h4 class="color-grey m-none">{{ row.name }}</h4>
            </router-link>
          </ak-table-column>

          <!-- Status -->
          <ak-table-column label="Status" field="status">
            <router-link
              class="block p-small"
              :to="{ name: 'editSurvey', params: { surveyId: row._id } }"
            >
              <ak-tag v-if="row.status === 'unpublished'" color="grey"
                >Draft</ak-tag
              >
              <ak-tag
                v-if="row.status === 'published' && !row.isActive"
                color="danger"
                >Closed</ak-tag
              >
              <ak-tag
                v-if="row.status === 'published' && row.isActive"
                color="success"
                >Published & Live</ak-tag
              >
              <ak-tag
                v-if="row.access === 'invite'"
                color="warning"
                class="ml-tiny"
                >Invite Only</ak-tag
              >
            </router-link>
          </ak-table-column>

          <!-- Last Edited -->
          <ak-table-column label="Last Edited">
            <router-link
              class="block p-small"
              :to="{ name: 'editSurvey', params: { surveyId: row._id } }"
            >
              <em> Edited {{ fromNow(row.updatedOn) }} </em>
            </router-link>
          </ak-table-column>

          <!-- Duration -->
          <ak-table-column label="Duration">
            <router-link
              class="block p-small"
              :to="{ name: 'editSurvey', params: { surveyId: row._id } }"
            >
              Starts
              <strong>{{ formatDate(row.start_date, 'DD/MM/YYYY') }}</strong>
              - Ends
              <strong>{{ formatDate(row.end_date, 'DD/MM/YYYY') }}</strong>
            </router-link>
          </ak-table-column>

          <!-- Credits -->
          <ak-table-column label="Credits" numeric>
            <router-link
              class="block p-small"
              :to="{ name: 'editSurvey', params: { surveyId: row._id } }"
            >
              <strong>{{ row.credit.value }}</strong>
            </router-link>
          </ak-table-column>

          <!-- Completed -->
          <ak-table-column label="Completed" numeric>
            <router-link
              class="block p-small"
              :to="{ name: 'editSurvey', params: { surveyId: row._id } }"
            >
              <strong>
                {{ row.current_completion_value
                }}<span v-if="row.completion_limit.enabled"
                  >/{{ row.completion_limit.value }}</span
                >
              </strong>
            </router-link>
          </ak-table-column>

          <!-- Dropdown Menu (Edit, Clone, Delete) -->
          <ak-table-column mini label="">
            <ak-dropdown aria-role="list" position-x="right">
              <template #trigger>
                <ak-button circle single-icon color="transparent">
                  <ak-icon type="more-vertical" />
                </ak-button>
              </template>
              <template #default>
                <ak-dropdown-item aria-role="listitem" has-link>
                  <router-link
                    :to="{ name: 'editSurvey', params: { surveyId: row._id } }"
                  >
                    Edit Survey
                  </router-link>
                </ak-dropdown-item>
                <ak-dropdown-item
                  aria-role="listitem"
                  @click.stop.prevent="cloneModal(row)"
                >
                  Clone Survey
                </ak-dropdown-item>
                <ak-dropdown-item
                  aria-role="listitem"
                  class="color-danger"
                  @click.stop.prevent="deleteDialog(row)"
                >
                  Delete Survey
                </ak-dropdown-item>
              </template>
            </ak-dropdown>
          </ak-table-column>
        </template>
      </ak-table>
    </ak-box>
    <ak-fab
      tag="router-link"
      :to="{ name: 'newSurvey' }"
      icon="plus"
      position-x="center"
      position-y="bottom"
    />
    <ak-clone-survey-modal
      v-model:is-active="openCloneModal"
      :account="activeAccount._id"
      :agency="activeAgency._id"
      :survey="cloneSurvey"
    />
  </ak-basic-container>
</template>

<script>
import { ref, defineComponent, computed, onMounted } from 'vue'
import BasicContainer from '../containers/BasicContainer.vue'
import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import Form from '@akerolabs/akero-vue/src/components/form/Form.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import ButtonGroup from '@akerolabs/akero-vue/src/components/buttonGroup/ButtonGroup.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import Table from '@akerolabs/akero-vue/src/components/table/Table.vue'
import TableColumn from '@akerolabs/akero-vue/src/components/table/TableColumn.vue'
import Tag from '@akerolabs/akero-vue/src/components/tag/Tag.vue'
import Dropdown from '@akerolabs/akero-vue/src/components/dropdown/Dropdown.vue'
import DropdownItem from '@akerolabs/akero-vue/src/components/dropdown/DropdownItem.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import Fab from '@akerolabs/akero-vue/src/components/fab/Fab.vue'
import CloneSurveyModal from './CloneSurveyModal.vue'
import { fromNow, formatDate } from '@akerolabs/akero-vue/src/filters/date'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { DialogProgrammatic } from '@akerolabs/akero-vue/src/components/dialog'
import { success, error } from './utils'
import { isAllowed } from '@akerolabs/akero-vue/src/services/features.js'
import { useRouter } from '../../legacy/router'

export default defineComponent({
  name: 'AkSurveyList',
  components: {
    [BasicContainer.name]: BasicContainer,
    [Box.name]: Box,
    [Form.name]: Form,
    [Input.name]: Input,
    [ButtonGroup.name]: ButtonGroup,
    [Button.name]: Button,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Tag.name]: Tag,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [Icon.name]: Icon,
    [Fab.name]: Fab,
    [CloneSurveyModal.name]: CloneSurveyModal,
  },
  inheritAttrs: false,
  props: {
    /**
     * @type {Object}
     * @description The active account (drilled in or users)
     */
    activeAccount: {
      default: () => ({}),
      type: Object,
    },
    /**
     * @type {Object}
     * @description The active agency (drilled in or users)
     */
    activeAgency: {
      default: () => ({}),
      type: Object,
    },
    /**
     * @type {Object}
     * @description The users profile data
     */
    userProfile: {
      default: () => ({}),
      type: Object,
    },
  },
  /**
   * @type {Function}
   * @returns {Object}
   * @description Returns the default state of the component.
   */
  setup(props) {
    const router = useRouter()

    const loading = ref(true)
    const searchFilter = ref('')
    const currentPage = ref(1)
    const perPage = ref(20)
    const total = ref(0)
    const surveys = ref([])
    const filter = ref({
      status: '',
      endDate: '',
      access: '',
    })
    const cloneSurvey = ref({})
    const openCloneModal = ref(false)

    /**
     * @description Should the table show a line under each row
     * @type {Boolean}
     */
    const shouldDisplayTableRows = computed(() => {
      return surveys.value.length >= 1
    })

    /**
     * @returns {Object}
     * @description Returns the settings for the search part of the request
     * object to send to the akero API.
     */
    const searchOptions = computed(() => {
      const result = {}
      if (searchFilter.value.length > 0) {
        const searchLower = searchFilter.value.toLowerCase()
        result.name = JSON.stringify({ $regex: searchLower })
      }
      return result
    })

    /**
     * @return {Object} get an object for the filters ready for the api
     */
    const requestFilter = computed(() => {
      const data = {}
      if (filter.value.status !== '') {
        data.status = filter.value.status
      }
      if (filter.value.endDate !== '') {
        const endDate = { live: '$gte', close: '$lte' }
        data.end_date = JSON.stringify({
          [endDate[filter.value.endDate]]: new Date().toISOString(),
        })
      }
      if (filter.value.access !== '') {
        data.access = filter.value.access
      }
      return data
    })

    /**
     * @description Request to api object
     * @return {Object}
     */
    const surveyRequest = computed(() => {
      return {
        params: {
          ...searchOptions.value,
          ...requestFilter.value,
          per_page: perPage.value,
          page: currentPage.value,
          account: props.activeAccount._id,
          agency: props.activeAgency._id,
          overrideAccess: true,
        },
      }
    })

    /**
     * @description Api request to fetch surveys
     */
    const fetchSurveys = async () => {
      loading.value = true
      let surveysData = []
      let totalNum = 0
      let page = 1
      try {
        const { data } = await request.get('/survey', surveyRequest.value)
        surveysData = data.data
        totalNum = data.meta.pagination.total
        page = data.meta.pagination.current
      } catch (ex) {
        surveysData = []
        console.log(ex)
        error('Error failed to load requests')
      }
      surveys.value = surveysData
      currentPage.value = page
      total.value = totalNum
      loading.value = false
    }

    /**
     * @description Called by the table when a user updates the page they want to see
     * @param {Number} page
     */
    const onPageChange = async (page) => {
      currentPage.value = page
      await fetchSurveys()
    }

    /**
     * @description update the filter object
     * @param {String} key
     * @param {String} value
     */
    const setFilter = async (key, value) => {
      if (filter.value[key] === value) {
        filter.value[key] = ''
      } else {
        filter.value[key] = value
      }
      await fetchSurveys()
    }

    /**
     * @description Dialog to handle deleting
     * @param {Object} survey
     */
    const deleteDialog = async (survey) => {
      DialogProgrammatic.prompt({
        title: `Delete Survey?`,
        message: `
          <p>Permanently delete your Survey ${survey.name}.</p>
          <small><strong>Please type DELETE to confirm</strong></small>
        `,
        inputAttrs: {
          pattern: 'DELETE',
        },
        trapFocus: true,
        type: 'danger',
        hasIcon: true,
        size: 'small',
        confirmText: `Yes, Delete Survey`,
        cancelText: `No, Keep Survey`,
        onConfirm: async () => {
          try {
            await request.delete(`/survey/${survey._id}`)
            success('Success your survey has been deleted.')
          } catch (ex) {
            console.log(ex)
            error('Failed to delete survey.')
          }
          await fetchSurveys()
        },
      })
    }

    /**
     * @description Set the working survey to clone and open the modal
     * @param {Object} survey
     */
    const cloneModal = (survey) => {
      cloneSurvey.value = survey
      openCloneModal.value = true
    }

    onMounted(async () => {
      const check = await isAllowed('surveys')
      if (!check.access) {
        router.push('')
      }
      await fetchSurveys()
    })

    return {
      searchFilter,
      loading,
      currentPage,
      perPage,
      total,
      shouldDisplayTableRows,
      surveys,
      onPageChange,
      fromNow,
      formatDate,
      filter,
      setFilter,
      deleteDialog,
      openCloneModal,
      cloneModal,
      fetchSurveys,
      cloneSurvey,
    }
  },
})
</script>
